/**
 * Alternative to zplMapper.js for printers that can't be sent raw thermal
 * printer commands at all — either a thermal printer speaking a language we
 * don't generate (e.g. an Argox printer in PPLA/PPLB mode instead of PPLZ),
 * or, as importantly, a plain page-fed inkjet/laser printer (e.g. "HP Smart
 * Tank... PCL-3") that has no thermal command interpreter whatsoever and
 * only prints onto sheets via its normal Windows driver.
 *
 * Renders a full page of labels as HTML and prints it via QZ Tray's "pixel"
 * type, which goes through the printer's normal installed driver — the
 * driver (not our code) handles whatever translation the printer needs, the
 * same way the old GDI-based desktop app worked.
 *
 * Tiles NoOfColumns x NoOfRows labels per physical page (A4 by default),
 * starting a new page once a page's grid is full — this is the page/sheet
 * layout model the old desktop app's ItemsPerPage = NoOfRows * NoOfColumns
 * was originally for, which the thermal continuous-roll code (zplMapper.js)
 * has no equivalent of, since a roll has no concept of "pages".
 *
 * Bonus: since real HTML/CSS does actual text measurement and wrapping,
 * this sidesteps the whole class of line-wrap-estimation bugs zplMapper.js
 * has to approximate (ZPL has no live measurement available to us).
 */
import JsBarcode from "jsbarcode";

const FIELD_NAME_TO_PRODUCT_KEY = {
  "Retail Price": "RetailPrice",
  "Supply Price": "SupplyPrice",
  "Sub Category": "SubCategory",
  "Vendor Code": "VendorCode",
  "Vendor Name": "VendorName",
  "Store Code": "StoreCode",
  "Store Name": "StoreName",
  "Transaction No": "TransactionNo",
  "Transaction Date": "TransactionDate",
};

// Standard page sizes, in mm — A4 default since that's standard in India;
// exposed as a param so a Letter-tray store isn't stuck with the wrong size.
const PAGE_SIZES_MM = {
  A4: { width: 210, height: 297 },
  LETTER: { width: 215.9, height: 279.4 },
};

/**
 * Centralized page-size resolution, used both here (to size the HTML page
 * div) and by BarcodePrint.js (to tell QZ Tray the physical page size to
 * print at) — one source of truth instead of two copies drifting apart.
 *
 * When NoOfRows is 1, there's no grid to tile vertically — the "page" is just
 * one row of labels. That's the continuous-roll case fed through a
 * page-oriented driver one row at a time (e.g. a thermal printer's driver,
 * which already knows the label's exact size), including multi-up rolls
 * (e.g. 3-across), as opposed to a real multi-label sheet (A4/Letter), which
 * needs an actual named page size to tile the grid onto. Sizing a roll row as
 * A4 would feed ~297mm of blank labels after every printed row.
 */
export function getPageDimensionsMm(barcodeSettings, pageSize) {
  const columns = Math.max(1, Number(barcodeSettings.NoOfColumns) || 1);
  const rows = Math.max(1, Number(barcodeSettings.NoOfRows) || 1);

  if (rows === 1) {
    const pageLeftMm = Number(barcodeSettings.LeftMargin) || 0;
    const pageTopMm = Number(barcodeSettings.TopMargin) || 0;
    const labelWidthMm = Number(barcodeSettings.LabelWidth) || 50;
    const labelHeightMm = Number(barcodeSettings.LabelHeight) || 30;
    const horizontalSpaceMm = Number(barcodeSettings.HorizontalSpace) || 0;
    return {
      width:
        pageLeftMm + columns * labelWidthMm + (columns - 1) * horizontalSpaceMm,
      height: labelHeightMm + pageTopMm,
    };
  }

  return PAGE_SIZES_MM[pageSize] || PAGE_SIZES_MM.A4;
}

function fieldValueFor(product, field, noOfCharacter) {
  const key = FIELD_NAME_TO_PRODUCT_KEY[field.FieldName] || field.FieldName;
  const raw = product && product.hasOwnProperty(key) ? product[key] : "";
  const displayName = field.FieldDisplayName || "";
  let text = displayName + (raw === null || raw === undefined ? "" : raw);

  if (noOfCharacter && text.length > noOfCharacter) {
    text = text.substring(0, noOfCharacter);
  }
  return text;
}

function getOrderedFields(printFields, position) {
  return (printFields || [])
    .filter(
      (f) => f.FieldSelected && (f.FieldPosition || "").toLowerCase() === position
    )
    .sort((a, b) => (a.FieldSequence || 0) - (b.FieldSequence || 0));
}

function escapeHtml(text) {
  return String(text == null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// CSS defines 96px == 1in == 25.4mm, always, regardless of real screen DPI —
// so measuring an element styled in mm and converting back via this ratio
// gives an accurate physical height, not just a guess like zplMapper.js's
// estimateLineCount has to make for ZPL's non-measurable built-in font.
const CSS_PX_PER_MM = 96 / 25.4;

function measureHeightMm(text, fontSizeMm, bold, widthMm) {
  const el = document.createElement("div");
  el.style.position = "absolute";
  el.style.visibility = "hidden";
  el.style.left = "-9999px";
  el.style.top = "0";
  el.style.width = widthMm + "mm";
  el.style.fontSize = fontSizeMm + "mm";
  el.style.fontWeight = bold ? "bold" : "normal";
  el.style.fontFamily = "Arial, Helvetica, sans-serif";
  el.style.lineHeight = "1.15";
  el.style.whiteSpace = "normal";
  el.style.wordBreak = "break-word";
  el.textContent = text;
  document.body.appendChild(el);
  const heightPx = el.offsetHeight;
  document.body.removeChild(el);
  return heightPx / CSS_PX_PER_MM;
}

function barcodeSvgMarkup(value, barWidthScale) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const moduleWidthPx = Math.max(1, Math.min(10, barWidthScale));
  try {
    JsBarcode(svg, String(value || "0"), {
      format: "CODE128",
      displayValue: false,
      margin: 0,
      width: moduleWidthPx,
      height: 100,
    });
  } catch (err) {
    return "";
  }
  // Scale by height only (via the wrapping container) so bar proportions
  // aren't distorted — width stays natural/auto.
  svg.setAttribute("style", "height:100%;width:auto;display:block;");
  svg.removeAttribute("width");
  svg.removeAttribute("height");
  return svg.outerHTML;
}

// Builds the positioned <div> markup for one item's label content, anchored
// at (originXMm, originYMm) — the top-left corner of its grid cell.
function buildLabelHtml(barcodeSettings, product, originXMm, originYMm, labelWidthMm) {
  const leftMarginMm = Number(barcodeSettings.LeftMarginInsideLabel) || 0;
  const topMarginMm = Number(barcodeSettings.TopMarginInsideLabel) || 0;
  const barcodeHeightMm = Number(barcodeSettings.BarcodeHeight) || 10;
  const barWidthScale = Math.min(
    10,
    Math.max(1, Math.round(barcodeSettings.BarWidthScale) || 2)
  );
  const noOfCharacter = barcodeSettings.NoOfCharacter || 100;
  const gapMm =
    barcodeSettings.MarginBetweenField != null
      ? Number(barcodeSettings.MarginBetweenField)
      : 1;
  const defaultFontMm = 3;
  const contentWidthMm = Math.max(1, labelWidthMm - leftMarginMm);
  const leftMm = originXMm + leftMarginMm;

  const aboveFields = getOrderedFields(barcodeSettings.PrintFields, "above barcode");
  const belowFields = getOrderedFields(barcodeSettings.PrintFields, "below barcode");

  let yMm = originYMm + topMarginMm;
  let html = "";

  const drawFields = (fields) => {
    fields.forEach((field) => {
      const fontMm = Number(field.FieldFontSize) || defaultFontMm;
      const bold = (field.FieldFontStyle || "").toLowerCase() === "bold";
      const text = fieldValueFor(product, field, noOfCharacter);
      const heightMm = measureHeightMm(text, fontMm, bold, contentWidthMm);

      html += `<div style="position:absolute;left:${leftMm}mm;top:${yMm}mm;width:${contentWidthMm}mm;font-size:${fontMm}mm;font-weight:${
        bold ? "bold" : "normal"
      };font-family:Arial,Helvetica,sans-serif;line-height:1.15;word-break:break-word;color:#000;">${escapeHtml(
        text
      )}</div>`;

      yMm += heightMm + gapMm;
    });
  };

  drawFields(aboveFields);

  // Barcode encodes SKU, matching zplMapper.js's behaviour.
  html += `<div style="position:absolute;left:${leftMm}mm;top:${yMm}mm;width:${contentWidthMm}mm;height:${barcodeHeightMm}mm;">${barcodeSvgMarkup(
    product.SKU,
    barWidthScale
  )}</div>`;
  yMm += barcodeHeightMm + gapMm;

  drawFields(belowFields);

  return html;
}

/**
 * Builds one HTML page per sheet of paper, tiling NoOfColumns x NoOfRows
 * labels per page (repeated NoOfPrints times per item), starting a new page
 * once the grid fills — the page/sheet layout for page-fed printers (inkjet,
 * laser, or any printer without a thermal command interpreter) as opposed
 * to zplMapper.js's continuous-roll layout. Returns [] if nothing to print.
 *
 * pageSize: "A4" (default) or "LETTER".
 */
export function generateLabelHtmlPages(barcodeSettings, printDataList, pageSize) {
  if (!barcodeSettings) {
    throw new Error("Barcode settings are not loaded.");
  }
  if (!printDataList || printDataList.length === 0) {
    return [];
  }

  const page = getPageDimensionsMm(barcodeSettings, pageSize);
  const pageLeftMm = Number(barcodeSettings.LeftMargin) || 0;
  const pageTopMm = Number(barcodeSettings.TopMargin) || 0;
  const labelWidthMm = Number(barcodeSettings.LabelWidth) || 50;
  const labelHeightMm = Number(barcodeSettings.LabelHeight) || 30;
  const horizontalSpaceMm = Number(barcodeSettings.HorizontalSpace) || 0;
  const verticalSpaceMm = Number(barcodeSettings.VerticalSpace) || 0;
  const columnPitchMm = labelWidthMm + horizontalSpaceMm;
  const rowPitchMm = labelHeightMm + verticalSpaceMm;
  const columns = Math.max(1, Number(barcodeSettings.NoOfColumns) || 1);
  const rows = Math.max(1, Number(barcodeSettings.NoOfRows) || 1);
  const perPage = columns * rows;

  const units = [];
  printDataList.forEach((product) => {
    const copies = Math.max(1, Number(product.NoOfPrints) || 1);
    for (let i = 0; i < copies; i++) units.push(product);
  });

  const pages = [];
  for (let i = 0; i < units.length; i += perPage) {
    const pageItems = units.slice(i, i + perPage);
    let bodyHtml = "";
    for (let idx = 0; idx < pageItems.length; idx++) {
      const col = idx % columns;
      const row = Math.floor(idx / columns);
      const originXMm = pageLeftMm + col * columnPitchMm;
      const originYMm = pageTopMm + row * rowPitchMm;
      bodyHtml += buildLabelHtml(
        barcodeSettings,
        pageItems[idx],
        originXMm,
        originYMm,
        labelWidthMm
      );
    }
    pages.push(
      `<div style="position:relative;width:${page.width}mm;height:${page.height}mm;overflow:hidden;">${bodyHtml}</div>`
    );
  }

  return pages;
}
